/**
 * MeGrowth — Phase 3 baby growth tracker
 *   /me/health/growth
 *   • WHO percentile lookup (weight & height for age)
 *   • Growth log CRUD
 *   • One-click "Seed India IAP vaccine schedule" from DOB
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Baby, Ruler, Scales, Users, Plus, Trash, Syringe,
  ChartLine, Sparkle, CheckCircle,
} from "@phosphor-icons/react";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function prettyDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}
function ageMonths(dob) {
  if (!dob) return null;
  const d0 = new Date(dob + "T00:00:00");
  const now = new Date();
  return Math.max(0, (now.getFullYear() - d0.getFullYear()) * 12 + (now.getMonth() - d0.getMonth()));
}

const BUCKET_META = {
  low:         { color: "#DC2626", bg: "#FEE2E2", label: "Below P3" },
  "below-avg": { color: "#EA580C", bg: "#FFEDD5", label: "Below avg" },
  normal:      { color: "#15803D", bg: "#DCFCE7", label: "Normal" },
  "above-avg": { color: "#0891B2", bg: "#CFFAFE", label: "Above avg" },
  high:        { color: "#7C3AED", bg: "#EDE9FE", label: "Above P97" },
};

function BackHeader({ to, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight text-[#111827]">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

export function BabyGrowth() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), weight_kg: "", height_cm: "", head_circ_cm: "", notes: "" });
  const [seedBusy, setSeedBusy] = useState(false);

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      const baby = list.find(l => l.kind === "baby");
      if (baby && !selectedId) setSelectedId(baby.id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) return;
    try {
      const r = await custApi.get(`/me/growth-logs?loved_one_id=${selectedId}`);
      setLogs(r.data.logs || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [selectedId]);

  const babies = lovedOnes.filter(l => l.kind === "baby");
  const currentLO = babies.find(b => b.id === selectedId);

  const save = async () => {
    if (!form.weight_kg && !form.height_cm && !form.head_circ_cm) {
      return toast.error("Enter at least one measurement");
    }
    try {
      await custApi.post("/me/growth-logs", {
        loved_one_id: selectedId,
        date: form.date,
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        head_circ_cm: form.head_circ_cm === "" ? null : Number(form.head_circ_cm),
        notes: form.notes.trim() || null,
      });
      toast.success("Growth logged");
      setForm({ date: todayISO(), weight_kg: "", height_cm: "", head_circ_cm: "", notes: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await custApi.delete(`/me/growth-logs/${id}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const seedIAP = async () => {
    if (!currentLO?.date_of_birth) {
      return toast.error("Add baby's date of birth on Profile first.");
    }
    if (!window.confirm(`Add the full India IAP vaccine schedule for ${currentLO.name}?`)) return;
    setSeedBusy(true);
    try {
      const r = await custApi.post("/me/vaccine-reminders/seed-india-baby", { loved_one_id: selectedId });
      toast.success(`${r.data.added} vaccines added to reminders`);
    } catch (e) { toast.error(formatErr(e)); }
    setSeedBusy(false);
  };

  const latest = logs[logs.length - 1];

  if (babies.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4">
        <BackHeader to="/me/health" title="Baby growth"/>
        <div className="border border-dashed border-[#2563EB] bg-[#DBEAFE] rounded-md p-4 text-center">
          <Baby size={24} weight="duotone" className="mx-auto mb-1 text-[#2563EB]"/>
          <div className="text-sm font-semibold text-[#1A1A1A]">Add your baby</div>
          <div className="text-[11px] text-[#5C5A55] mt-1">
            On Profile → Health Profile, add a loved one with Care Kind = <b>Baby</b>, DOB and gender.
          </div>
          <Link to="/me/profile#health-profile"
                className="mt-3 inline-block h-9 px-4 leading-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md text-xs font-medium">
            Go to Profile
          </Link>
        </div>
      </div>
    );
  }

  const age = ageMonths(currentLO?.date_of_birth);

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-growth">
      <BackHeader to="/me/health" title="Baby growth"
                    subtitle="WHO percentiles · vaccine seeder"/>

      {/* Picker */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5 font-semibold">
          Which baby?
        </label>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
          {babies.map(lo => {
            const on = lo.id === selectedId;
            return (
              <button key={lo.id} onClick={() => setSelectedId(lo.id)}
                      data-testid={`growth-picker-${lo.id}`}
                      className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 ${
                        on ? "bg-[#2563EB] text-white border-[#2563EB]"
                           : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}>
                <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                  on ? "bg-white/25" : "bg-[#DBEAFE] text-[#2563EB]"
                }`}>{lo.name?.[0]?.toUpperCase() || "?"}</span>
                {lo.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Baby info card */}
      {currentLO && (
        <div className="rounded-3xl p-5 bg-gradient-to-br from-[#DBEAFE] via-[#BFDBFE] to-[#93C5FD] shadow-[0_20px_50px_rgba(37,99,235,0.15)]"
             data-testid="growth-baby-card">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-2xl bg-white text-[#2563EB] font-black text-2xl inline-flex items-center justify-center shrink-0">
              {currentLO.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl text-[#1E3A8A]">{currentLO.name}</div>
              <div className="text-[11px] text-[#1E40AF] font-medium mt-0.5">
                {currentLO.date_of_birth ? `Born ${prettyDate(currentLO.date_of_birth)}` : "DOB not set"}
                {age != null ? ` · ${age === 0 ? "<1" : age} mo` : ""}
                {currentLO.gender ? ` · ${currentLO.gender}` : ""}
              </div>
              {!currentLO.date_of_birth && (
                <Link to="/me/profile#health-profile"
                      className="mt-2 inline-block text-[10px] px-2 py-1 rounded bg-white text-[#DC2626] font-bold uppercase tracking-widest">
                  Add DOB →
                </Link>
              )}
            </div>
          </div>

          {latest && (
            <div className="grid grid-cols-3 gap-2 mt-4">
              <BabyStat Icon={Scales} label="Weight"
                        value={latest.weight_kg != null ? `${latest.weight_kg} kg` : "—"}
                        pct={latest.weight_pct}/>
              <BabyStat Icon={Ruler} label="Height"
                        value={latest.height_cm != null ? `${latest.height_cm} cm` : "—"}
                        pct={latest.height_pct}/>
              <BabyStat Icon={Sparkle} label="Head"
                        value={latest.head_circ_cm != null ? `${latest.head_circ_cm} cm` : "—"}/>
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setShowForm(true)}
                data-testid="growth-add"
                className="h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl text-sm font-medium inline-flex items-center justify-center gap-1.5">
          <Plus size={13} weight="bold"/> Log measurements
        </button>
        <button onClick={seedIAP} disabled={seedBusy}
                data-testid="growth-seed-iap"
                className="h-11 bg-white border-2 border-[#DC2626] text-[#DC2626] hover:bg-[#FEE2E2] rounded-xl text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Syringe size={13} weight="fill"/> {seedBusy ? "Adding…" : "Seed IAP schedule"}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="border-2 border-dashed border-[#2563EB] rounded-md p-3 bg-[#EFF6FF] space-y-2" data-testid="growth-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Date</label>
              <input type="date" value={form.date}
                     onChange={e => setForm(s => ({ ...s, date: e.target.value }))}
                     data-testid="growth-date"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Weight (kg)</label>
              <input type="number" step="0.01" value={form.weight_kg}
                     onChange={e => setForm(s => ({ ...s, weight_kg: e.target.value }))}
                     data-testid="growth-weight"
                     placeholder="e.g. 5.4"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Height (cm)</label>
              <input type="number" step="0.1" value={form.height_cm}
                     onChange={e => setForm(s => ({ ...s, height_cm: e.target.value }))}
                     data-testid="growth-height"
                     placeholder="e.g. 58"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Head (cm)</label>
              <input type="number" step="0.1" value={form.head_circ_cm}
                     onChange={e => setForm(s => ({ ...s, head_circ_cm: e.target.value }))}
                     data-testid="growth-head"
                     placeholder="e.g. 40"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
          </div>
          <input value={form.notes}
                 onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                 placeholder="Notes"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={save} data-testid="growth-save"
                    className="h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md text-sm font-medium">
              Save
            </button>
            <button onClick={() => setShowForm(false)}
                    className="h-10 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold mb-2">
          History ({logs.length})
        </div>
        {logs.length === 0 ? (
          <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
            <ChartLine size={22} color="#8A8780" className="mx-auto mb-1"/>
            <div className="text-xs text-[#5C5A55]">No measurements logged yet.</div>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="growth-list">
            {[...logs].reverse().map(g => (
              <div key={g.id} data-testid={`growth-row-${g.id}`}
                   className="border border-[#E5E2D9] rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[12px] font-semibold text-[#1A1A1A]">
                    {prettyDate(g.date)}
                    {g.age_months != null && <span className="ml-1.5 text-[10px] text-gray-500">{g.age_months} mo</span>}
                  </div>
                  <button onClick={() => remove(g.id)} data-testid={`growth-del-${g.id}`}
                          className="p-1.5 rounded hover:bg-[#FEE2E2]">
                    <Trash size={12} color="#DC2626"/>
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {g.weight_kg != null && (
                    <MeasChip label="Weight" val={`${g.weight_kg} kg`} pct={g.weight_pct}/>
                  )}
                  {g.height_cm != null && (
                    <MeasChip label="Height" val={`${g.height_cm} cm`} pct={g.height_pct}/>
                  )}
                  {g.head_circ_cm != null && (
                    <MeasChip label="Head" val={`${g.head_circ_cm} cm`}/>
                  )}
                </div>
                {g.notes && <div className="text-[11px] text-[#5C5A55] mt-1">{g.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BabyStat({ Icon, label, value, pct }) {
  const bucket = pct?.bucket ? BUCKET_META[pct.bucket] : null;
  return (
    <div className="bg-white/60 backdrop-blur rounded-xl p-2">
      <Icon size={12} weight="fill" className="text-[#1D4ED8]"/>
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#1E40AF] mt-0.5">{label}</div>
      <div className="text-[14px] font-black text-[#1E3A8A] leading-tight">{value}</div>
      {bucket && (
        <div className="mt-1 inline-block text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest"
             style={{ backgroundColor: bucket.bg, color: bucket.color }}>
          {bucket.label}
        </div>
      )}
    </div>
  );
}

function MeasChip({ label, val, pct }) {
  const bucket = pct?.bucket ? BUCKET_META[pct.bucket] : null;
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] font-medium">
      {label}: <span className="font-bold">{val}</span>
      {bucket && (
        <span className="ml-1.5 px-1.5 py-0.5 rounded font-bold uppercase tracking-widest text-[9px]"
              style={{ backgroundColor: bucket.bg, color: bucket.color }}>
          {bucket.label}
        </span>
      )}
    </span>
  );
}
